import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiPlus, 
  FiEdit3, 
  FiTrash2, 
  FiCalendar, 
  FiEye, 
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiClock,
  FiUsers,
  FiMapPin,
  FiPlay,
  FiPause,
  FiCheckCircle,
  FiXCircle,
  FiUserCheck
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { reuniaoService } from '../../services/api';
import {
  parseDataReuniaoLocal,
  formatarDataReuniaoCurta,
  isMesmoDiaCalendario,
  isDataReuniaoPassada,
} from '../../utils/reuniaoDate';
import './Reunioes.css';

const Reunioes = () => {
  const navigate = useNavigate();
  const [reunioes, setReunioes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroData, setFiltroData] = useState('todos');
  const [reuniaoParaExcluir, setReuniaoParaExcluir] = useState(null);
  const [mostrarModalExclusao, setMostrarModalExclusao] = useState(false);

  useEffect(() => {
    carregarReunioes();
  }, []);

  const carregarReunioes = async () => {
    try {
      setLoading(true);
      const response = await reuniaoService.getAll();
      if (response.success) {
        setReunioes(response.data || []);
      } else {
        toast.error('Erro ao carregar reuniões');
      }
    } catch (error) {
      console.error('Erro ao carregar reuniões:', error);
      toast.error('Erro ao carregar reuniões');
    } finally {
      setLoading(false);
    }
  };

  const confirmarExclusao = (reuniao) => {
    setReuniaoParaExcluir(reuniao);
    setMostrarModalExclusao(true);
  };

  const excluirReuniao = async () => {
    if (!reuniaoParaExcluir) return;

    try {
      const response = await reuniaoService.delete(reuniaoParaExcluir.id);
      if (response.success) {
        toast.success('Reunião excluída com sucesso!');
        carregarReunioes();
      } else {
        toast.error(response.message || 'Erro ao excluir reunião');
      }
    } catch (error) {
      console.error('Erro ao excluir reunião:', error);
      toast.error('Erro ao excluir reunião');
    } finally {
      setMostrarModalExclusao(false);
      setReuniaoParaExcluir(null);
    }
  };

  const alterarStatus = async (reuniao, novoStatus) => {
    try {
      const dadosAtualizados = {
        ...reuniao,
        status: novoStatus
      };

      const response = await reuniaoService.update(reuniao.id, dadosAtualizados);
      if (response.success) {
        toast.success(`Status alterado para: ${getStatusLabel(novoStatus)}`);
        carregarReunioes();
      } else {
        toast.error(response.message || 'Erro ao alterar status');
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status da reunião');
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'agendada': 'Agendada',
      'em_andamento': 'Em Andamento',
      'concluida': 'Concluída',
      'cancelada': 'Cancelada'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      'agendada': FiClock,
      'em_andamento': FiPlay,
      'concluida': FiCheckCircle,
      'cancelada': FiXCircle
    };
    return icons[status] || FiClock;
  };

  const reunioesFiltradas = reunioes.filter(reuniao => {
    const matchSearch = reuniao.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (reuniao.descricao && reuniao.descricao.toLowerCase().includes(searchTerm.toLowerCase())) ||
                       (reuniao.local && reuniao.local.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchStatus = filtroStatus === 'todos' || reuniao.status === filtroStatus;
    
    const dataReuniao = reuniao.data ?? reuniao.dataReuniao;
    const d = parseDataReuniaoLocal(dataReuniao);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const cmp = d ? new Date(d) : null;
    if (cmp) cmp.setHours(0, 0, 0, 0);

    let matchData = true;
    if (filtroData === 'hoje') {
      matchData = isMesmoDiaCalendario(dataReuniao, new Date());
    } else if (filtroData === 'proximas') {
      matchData = cmp != null && cmp.getTime() >= hoje.getTime();
    } else if (filtroData === 'passadas') {
      matchData = isDataReuniaoPassada(dataReuniao);
    }
    
    return matchSearch && matchStatus && matchData;
  });

  const formatarData = (data) => formatarDataReuniaoCurta(data);

  const formatarHora = (hora) => {
    if (hora == null || hora === '') return '—:—';
    const d = typeof hora === 'object' && hora instanceof Date ? hora : new Date(hora);
    if (isNaN(d.getTime())) return '—:—';
    if (typeof hora === 'string' && hora.length <= 8 && /^\d{1,2}:\d{2}/.test(hora)) return hora.substring(0, 5);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatarDataCriacao = (reuniao) => {
    const raw = reuniao.created_at ?? reuniao.createdAt;
    if (raw == null || raw === '') return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
  };

  const isReuniaoHoje = (data) => isMesmoDiaCalendario(data, new Date());

  const isReuniaoPassada = (data) => isDataReuniaoPassada(data);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <FiRefreshCw className="spin" />
          <p>Carregando reuniões...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reunioes-page">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <FiCalendar className="page-icon" />
            Gerenciar Reuniões
          </h1>
          <p>Organize e controle as reuniões da congregação</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => navigate('/reunioes/cadastro')}
        >
          <FiPlus /> Nova Reunião
        </button>
      </div>

      <div className="page-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por título, descrição ou local..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <FiFilter className="filter-icon" />
            <select 
              value={filtroStatus} 
              onChange={(e) => setFiltroStatus(e.target.value)}
            >
              <option value="todos">Todos Status</option>
              <option value="agendada">Agendadas</option>
              <option value="em_andamento">Em Andamento</option>
              <option value="concluida">Concluídas</option>
              <option value="cancelada">Canceladas</option>
            </select>
          </div>

          <div className="filter-group">
            <select 
              value={filtroData} 
              onChange={(e) => setFiltroData(e.target.value)}
            >
              <option value="todos">Todas as Datas</option>
              <option value="hoje">Hoje</option>
              <option value="proximas">Próximas</option>
              <option value="passadas">Passadas</option>
            </select>
          </div>
        </div>

        <button
          className="btn-refresh"
          onClick={carregarReunioes}
          title="Atualizar lista"
        >
          <FiRefreshCw />
        </button>
      </div>

      <div className="reunioes-stats">
        <div className="stat-card">
          <span className="stat-number">{reunioes.length}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-card pending">
          <span className="stat-number">{reunioes.filter(r => r.status === 'agendada').length}</span>
          <span className="stat-label">Agendadas</span>
        </div>
        <div className="stat-card active">
          <span className="stat-number">{reunioes.filter(r => r.status === 'em_andamento').length}</span>
          <span className="stat-label">Em Andamento</span>
        </div>
        <div className="stat-card completed">
          <span className="stat-number">{reunioes.filter(r => r.status === 'concluida').length}</span>
          <span className="stat-label">Concluídas</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{reunioesFiltradas.length}</span>
          <span className="stat-label">Filtradas</span>
        </div>
      </div>

      {reunioesFiltradas.length === 0 ? (
        <div className="empty-state">
          <FiCalendar className="empty-icon" />
          <h3>Nenhuma reunião encontrada</h3>
          <p>
            {searchTerm || filtroStatus !== 'todos' || filtroData !== 'todos'
              ? 'Tente ajustar os filtros de busca'
              : 'Comece agendando a primeira reunião'
            }
          </p>
          {!searchTerm && filtroStatus === 'todos' && filtroData === 'todos' && (
            <button
              className="btn-primary"
              onClick={() => navigate('/reunioes/cadastro')}
            >
              <FiPlus /> Agendar Primeira Reunião
            </button>
          )}
        </div>
      ) : (
        <div className="reunioes-grid">
          {reunioesFiltradas.map(reuniao => {
            const StatusIcon = getStatusIcon(reuniao.status);
            const dataReuniao = reuniao.data ?? reuniao.dataReuniao;
            const isHoje = isReuniaoHoje(dataReuniao);
            const isPassada = isReuniaoPassada(dataReuniao);
            const horaInicio = reuniao.hora_inicio ?? reuniao.horaInicio;
            const horaFim = reuniao.hora_fim ?? reuniao.horaFim;
            const dataCriacaoStr = formatarDataCriacao(reuniao);
            
            return (
              <div 
                key={reuniao.id} 
                className={`reuniao-card ${reuniao.status} ${isHoje ? 'hoje' : ''} ${isPassada ? 'passada' : ''}`}
              >
                <div className="reuniao-header">
                  <div className="reuniao-status">
                    <StatusIcon className="status-icon" />
                    <span className="status-label">{getStatusLabel(reuniao.status)}</span>
                  </div>
                  <div className="reuniao-actions">
                    <button
                      className="btn-icon"
                      onClick={() => navigate(`/reunioes/${reuniao.id}`)}
                      title="Visualizar"
                    >
                      <FiEye />
                    </button>
                    <button
                      className="btn-icon presence"
                      onClick={() => navigate(`/presencas/gerenciar/${reuniao.id}`)}
                      title="Gerenciar Presenças"
                    >
                      <FiUserCheck />
                    </button>
                    <button
                      className="btn-icon edit"
                      onClick={() => navigate(`/reunioes/editar/${reuniao.id}`)}
                      title="Editar"
                    >
                      <FiEdit3 />
                    </button>
                    <button
                      className="btn-icon delete"
                      onClick={() => confirmarExclusao(reuniao)}
                      title="Excluir"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
                
                <div className="reuniao-body">
                  <h3 className="reuniao-titulo">{reuniao.titulo}</h3>
                  {reuniao.descricao && (
                    <p className="reuniao-descricao">{reuniao.descricao}</p>
                  )}
                  
                  <div className="reuniao-info">
                    <div className="info-item">
                      <FiCalendar />
                      <span>{formatarData(dataReuniao)}</span>
                      {isHoje && <span className="hoje-badge">Hoje</span>}
                    </div>
                    <div className="info-item">
                      <FiClock />
                      <span>{formatarHora(horaInicio)} – {formatarHora(horaFim)}</span>
                    </div>
                    {reuniao.local && (
                      <div className="info-item">
                        <FiMapPin />
                        <span>{reuniao.local}</span>
                      </div>
                    )}
                    <div className="info-item">
                      <FiUsers />
                      <span>
                        {(reuniao._count?.reuniaoParticipantes ?? reuniao.total_participantes ?? 0)} participante(s)
                      </span>
                    </div>
                  </div>
                </div>

                <div className="reuniao-footer">
                  <div className="status-actions">
                    {reuniao.status === 'agendada' && (
                      <>
                        <button
                          className="btn-status start"
                          onClick={() => alterarStatus(reuniao, 'em_andamento')}
                          title="Iniciar reunião"
                        >
                          <FiPlay /> Iniciar
                        </button>
                        <button
                          className="btn-status cancel"
                          onClick={() => alterarStatus(reuniao, 'cancelada')}
                          title="Cancelar reunião"
                        >
                          <FiXCircle /> Cancelar
                        </button>
                      </>
                    )}
                    
                    {reuniao.status === 'em_andamento' && (
                      <>
                        <button
                          className="btn-status complete"
                          onClick={() => alterarStatus(reuniao, 'concluida')}
                          title="Finalizar reunião"
                        >
                          <FiCheckCircle /> Finalizar
                        </button>
                        <button
                          className="btn-status pause"
                          onClick={() => alterarStatus(reuniao, 'agendada')}
                          title="Pausar reunião"
                        >
                          <FiPause /> Pausar
                        </button>
                      </>
                    )}
                    
                    {(reuniao.status === 'concluida' || reuniao.status === 'cancelada') && (
                      <button
                        className="btn-status reopen"
                        onClick={() => alterarStatus(reuniao, 'agendada')}
                        title="Reagendar reunião"
                      >
                        <FiClock /> Reagendar
                      </button>
                    )}
                  </div>
                  
                  {dataCriacaoStr && (
                    <small className="created-date">Criada em: {dataCriacaoStr}</small>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {mostrarModalExclusao && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirmar Exclusão</h3>
            </div>
            <div className="modal-body">
              <p>
                Tem certeza que deseja excluir a reunião <strong>{reuniaoParaExcluir?.titulo}</strong>?
              </p>
              <p className="warning">
                ⚠️ Esta ação não pode ser desfeita. Todos os registros de presença 
                e dados relacionados a esta reunião serão perdidos permanentemente.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setMostrarModalExclusao(false);
                  setReuniaoParaExcluir(null);
                }}
              >
                Cancelar
              </button>
              <button
                className="btn-danger"
                onClick={excluirReuniao}
              >
                <FiTrash2 /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reunioes;